<!--
  src/components/DialogMovePlanTasks.vue
  Stage 2 (bulk) — move already-placed plan tasks from their current date(s) to one new date.
  Opened from a plan tab's row action. Each selected row is a placement, so `id` is the
  StructuredPlanTask id (what the update is keyed on).
  One batched call — PUT api/Admin/StructuredPlan/PlanTasks:
    { Tasks: [{ Id, TaskDate, SortOrder, StartTime?, EndTime?, TimeWindow?, DurationMinutes? }] }
  The endpoint has no partial-update semantics: every field is written as sent. Only TaskDate
  comes from this dialog — SortOrder, the time fields and the per-program duration override are
  echoed back per row so moving a task can't quietly reset its position, clear its hours, or
  drop a duration an admin pinned for this program.
-->
<template>
  <div class="DialogMovePlanTasks" dir="rtl">
    <div class="modal-card" style="width: 460px;">
      <header class="modal-card-head">
        <p class="modal-card-title">העברת משימות לתאריך אחר</p>
        <button type="button" class="delete" @click="$emit('close')" />
      </header>

      <section class="modal-card-body">
        <p class="DialogMovePlanTasks__Count">
          <template v-if="tasks.length === 1">
            העברת המשימה <b>{{ singleName }}</b>.
          </template>
          <template v-else>
            נבחרו <b>{{ tasks.length }}</b> משימות להעברה.
          </template>
        </p>

        <p v-if="currentDatesLabel" class="DialogMovePlanTasks__From">
          תאריך נוכחי: {{ currentDatesLabel }}
        </p>

        <b-field label="תאריך יעד">
          <b-datepicker v-model="date" icon="calendar-today" placeholder="בחר תאריך" trap-focus editable />
        </b-field>

        <div class="note">
          שאר פרטי השיבוץ (חלון זמן, שעות, משך וסדר בתוך היום) יישארו ללא שינוי.
        </div>
      </section>

      <footer class="modal-card-foot">
        <b-button :loading="isLoading" :disabled="date == null" type="is-primary" label="העבר" @click="save" />
        <b-button :disabled="isLoading" label="ביטול" @click="$emit('close')" />
      </footer>
    </div>
  </div>
</template>

<script>
import { ToastProgrammatic as Toast } from "buefy";
import TasksService from "@/services/TasksService";
import moment from "moment";

export default {
  name: "DialogMovePlanTasks",
  props: {
    // placed plan rows (carry id, taskDate, sortOrder)
    tasks: { type: Array, default: () => [] },
    onSuccess: { type: Function, default: null },
  },
  data() {
    return {
      isLoading: false,
      // Seed with the selection's date when they all share one, so a same-day
      // time-window-only selection doesn't open on today by accident.
      date: this.initialDate(),
    };
  },
  computed: {
    singleName() {
      const t = this.tasks[0];
      return t ? t.taskName || t.name || `#${t.id}` : "";
    },
    // "12/03/2026" for one shared date, "3 תאריכים" when the selection spans days.
    currentDatesLabel() {
      const dates = [...new Set(this.tasks.map((t) => t.taskDate).filter(Boolean))];
      if (!dates.length) return "";
      if (dates.length === 1) return moment(dates[0]).format("DD/MM/YYYY");
      return `${dates.length} תאריכים`;
    },
  },
  methods: {
    initialDate() {
      const dates = [...new Set((this.tasks || []).map((t) => t.taskDate).filter(Boolean))];
      if (dates.length === 1) {
        const d = moment(dates[0]);
        if (d.isValid()) return d.toDate();
      }
      return new Date();
    },
    save() {
      if (this.date == null || !this.tasks.length) return;
      this.isLoading = true;
      // Local YYYY-MM-DD: formatting off the Date object (not toISOString) keeps JSON's
      // UTC conversion from shifting the day back for timezones east of GMT.
      const taskDate = moment(this.date).format("YYYY-MM-DD");
      TasksService.updateStructuredPlanTasks(
        this.tasks.map((t) => ({
          Id: t.id,
          TaskDate: taskDate,
          SortOrder: t.sortOrder,
          StartTime: t.startTime || null,
          EndTime: t.endTime || null,
          TimeWindow: t.timeWindow,
          // The row's OVERRIDE, not its resolved durationMinutes: null here means "inherit
          // the bank", which is exactly right for a row that was already inheriting. Echoing
          // the resolved value instead would pin the bank's duration onto every moved row.
          DurationMinutes: t.durationMinutesOverride != null ? t.durationMinutesOverride : null,
        }))
      )
        .then(() => {
          const msg = this.tasks.length === 1
            ? "המשימה הועברה לתאריך החדש!"
            : `${this.tasks.length} משימות הועברו לתאריך החדש!`;
          Toast.open({ type: "is-success", message: msg, duration: 4000 });
          if (this.onSuccess) this.onSuccess();
          this.$emit("close");
        })
        .catch((error) => {
          // All-or-nothing on the server: a bad row rejects the whole batch, nothing moved.
          const serverMsg = error && error.response && error.response.data
            && (error.response.data.Error || error.response.data.error);
          Toast.open({ type: "is-danger", message: serverMsg || "העברת המשימות נכשלה!", duration: 8000 });
        })
        .finally(() => { this.isLoading = false; });
    },
  },
};
</script>

<style scoped lang="scss">
.DialogMovePlanTasks__Count {
  margin-bottom: 6px;
}
.DialogMovePlanTasks__From {
  margin-bottom: 14px;
  font-size: 13px;
  color: #6b7b8c;
}
.note {
  margin-top: 16px;
  font-size: 13px;
  color: #6b7b8c;
}
</style>
